import {
  StateNode,
  Vec,
  createShapeId,
  maybeSnapToGrid
} from "@tldraw/editor";
import { startEditingShapeWithRichText } from "../../../tools/SelectTool/selectHelpers.mjs";
import { getDisplayValues } from "../../shared/getDisplayValues.mjs";
import {
  NOTE_ADJACENT_POSITION_SNAP_RADIUS,
  getAvailableNoteAdjacentPositions
} from "../noteHelpers.mjs";
class Pointing extends StateNode {
  static id = "pointing";
  dragged = false;
  info = {};
  markId = "";
  shape = {};
  shapeId;
  shapeCenter = new Vec();
  hasCreatedShape = false;
  onEnter() {
    const { editor } = this;
    this.hasCreatedShape = false;
    this.shapeId = createShapeId();
    this.markId = editor.markHistoryStoppingPoint(`creating_note:${this.shapeId}`);
    const noteUtil = editor.getShapeUtil("note");
    const dv = getDisplayValues(noteUtil, { props: noteUtil.getDefaultProps() });
    const center = editor.inputs.getOriginPagePoint().clone();
    const offset = getNoteShapeAdjacentPositionOffset(
      editor,
      center,
      editor.getResizeScaleFactor(),
      dv.noteWidth,
      dv.noteHeight
    );
    if (offset) {
      center.sub(offset);
    }
    this.shapeCenter = center;
    if (!editor.getInstanceState().isCoarsePointer) {
      this.ensureShapeCreated();
    }
  }
  // Create the note now if it hasn't been created yet (idempotent). The deferred
  // drag and release paths route through here; a max-shapes failure cancels the
  // gesture, matching the original onEnter behaviour.
  ensureShapeCreated() {
    if (this.hasCreatedShape) return;
    const shape = createNoteShape(this.editor, this.shapeId, this.shapeCenter);
    if (shape) {
      this.shape = shape;
      this.hasCreatedShape = true;
    } else {
      this.cancel();
    }
  }
  onPointerMove(info) {
    if (this.editor.inputs.getIsDragging()) {
      this.ensureShapeCreated();
      if (!this.hasCreatedShape) return;
      this.editor.setCurrentTool("select.translating", {
        ...info,
        target: "shape",
        shape: this.shape,
        onInteractionEnd: "note",
        isCreating: true,
        creatingMarkId: this.markId,
        onCreate: () => {
          startEditingShapeWithRichText(this.editor, this.shape.id);
        }
      });
    }
  }
  onPointerUp() {
    this.complete();
  }
  onLongPress() {
    if (this.editor.getInstanceState().isCoarsePointer) this.cancel();
  }
  onInterrupt() {
    this.cancel();
  }
  onComplete() {
    this.complete();
  }
  onCancel() {
    this.cancel();
  }
  complete() {
    this.ensureShapeCreated();
    if (!this.hasCreatedShape) return;
    if (this.editor.getInstanceState().isToolLocked) {
      this.parent.transition("idle");
    } else {
      startEditingShapeWithRichText(this.editor, this.shape.id, { info: this.info });
    }
  }
  cancel() {
    this.editor.bailToMark(this.markId);
    this.parent.transition("idle", this.info);
  }
}
function getNoteShapeAdjacentPositionOffset(editor, center, scale, noteWidth, noteHeight) {
  let min = NOTE_ADJACENT_POSITION_SNAP_RADIUS / editor.getZoomLevel();
  let offset;
  for (const pit of getAvailableNoteAdjacentPositions(editor, {
    rotation: 0,
    scale,
    extraHeight: 0,
    noteWidth,
    noteHeight
  })) {
    const deltaToPit = Vec.Sub(center, pit);
    const dist = deltaToPit.len();
    if (dist < min) {
      min = dist;
      offset = deltaToPit;
    }
  }
  return offset;
}
function createNoteShape(editor, id, center) {
  editor.createShape({
    id,
    type: "note",
    x: center.x,
    y: center.y,
    props: {
      scale: editor.getResizeScaleFactor()
    }
  });
  const shape = editor.getShape(id);
  if (!shape) return;
  editor.select(id);
  const bounds = editor.getShapeGeometry(shape).bounds;
  const newPoint = maybeSnapToGrid(
    new Vec(shape.x - bounds.width / 2, shape.y - bounds.height / 2),
    editor
  );
  editor.updateShapes([
    {
      id,
      type: "note",
      x: newPoint.x,
      y: newPoint.y
    }
  ]);
  return editor.getShape(id);
}
export {
  Pointing,
  createNoteShape,
  getNoteShapeAdjacentPositionOffset
};
//# sourceMappingURL=Pointing.mjs.map
